import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout, Field, FormError, FormNotice } from '../../components/Form.js';
import { ApiError, apiRequest } from '../../lib/api.js';

/** Story #2 — email verification. An account cannot sign in until it passes. */
export function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);

    try {
      await apiRequest('/auth/confirm', {
        method: 'POST',
        // The one call that tells the API which channel produced this
        // account. Absent is fine and means `direct`.
        body: { email, code, ...(params.get('via') ? { via: params.get('via') } : {}) },
        anonymous: true,
      });
      navigate('/signin?verified=1');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not verify that code.');
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setError(null);
    setNotice(null);

    try {
      await apiRequest('/auth/resend-code', { method: 'POST', body: { email }, anonymous: true });
      setNotice('We sent a new code. It can take a minute to arrive.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not send a new code.');
    }
  }

  return (
    <AuthLayout title="Verify your email" subtitle="We sent a six-digit code to your inbox.">
      <form onSubmit={onSubmit} noValidate>
        <Field label="Email" htmlFor="email">
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </Field>

        <Field label="Verification code" htmlFor="code">
          <input
            id="code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            required
          />
        </Field>

        <FormError message={error} />
        <FormNotice message={notice} />

        <button type="submit" disabled={busy} className="block">
          {busy ? 'Verifying…' : 'Verify email'}
        </button>

        <button type="button" className="secondary block" onClick={resend}>
          Send a new code
        </button>
      </form>
    </AuthLayout>
  );
}
